import { setMilliseconds, sub } from 'date-fns';
import { createWithEqualityFn as create } from 'zustand/traditional';

import {
  filtersForGroupType,
  receivingClientFilterKey,
  receivingGroupFilterKey,
} from 'components/insights/careTransitions/filters';
import { DATES, END_DATE, PREDECESSOR_GROUP, START_DATE } from 'constants/filterKeysConstants';
import GroupType from 'models/GroupType';

import { FilterOption, FilterState } from './types';

const now = setMilliseconds(new Date(), 0);
const thirtyDaysAgo = sub(now, { days: 30 });

export type NetworkPerformanceFilterState = FilterState<FilterOption[] | string | Date>;

type Actions = {
  setSelectedGroupType: (groupType: GroupType | undefined) => void;
  setPredecessorGroupTypeId: (id: string | undefined) => void;
  setSelectedFilters: (filters: NetworkPerformanceFilterState) => void;
  resetFilters: () => void;
  setExpanded: (expanded: boolean) => void;
};

type State = {
  selectedGroupType: GroupType | undefined;
  predecessorGroupTypeId: string | undefined;
  filters: NetworkPerformanceFilterState;
  expanded: boolean;
  actions: Actions;
};

export const useNetworkPerformanceStore = create<State>((set) => ({
  selectedGroupType: undefined,
  predecessorGroupTypeId: undefined,
  expanded: false,
  filters: {
    [START_DATE]: thirtyDaysAgo,
    [END_DATE]: now,
  },
  actions: {
    setSelectedGroupType: (groupType) => set({ selectedGroupType: groupType }),
    setPredecessorGroupTypeId: (id) => set({ predecessorGroupTypeId: id }),
    setSelectedFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),
    resetFilters: () =>
      set({
        filters: {
          [START_DATE]: thirtyDaysAgo,
          [END_DATE]: now,
        },
      }),
    setExpanded: (expanded) => set({ expanded }),
  },
}));

export const useNetworkPerformanceActions = () => useNetworkPerformanceStore((state) => state.actions);

export const getNetworkPerformanceAnalyticsFilters = ({
  filters,
  selectedGroupType,
  predecessorGroupTypeId,
}: State) => {
  if (!selectedGroupType || !predecessorGroupTypeId) return null;

  const activeFilters = filtersForGroupType(filters, selectedGroupType, PREDECESSOR_GROUP);
  const receivingClientKey = receivingClientFilterKey(selectedGroupType);
  const receivingGroupKey = receivingGroupFilterKey(selectedGroupType);

  const {
    [START_DATE]: start,
    [END_DATE]: end,
    [receivingClientKey]: providerClient,
    [receivingGroupKey]: group,
    ...rest
  } = activeFilters;

  return {
    ...rest,
    [DATES]: [{ start, end }],
    providerClient,
    group,
    predecessorGroupType: predecessorGroupTypeId,
    groupType: selectedGroupType.id,
  };
};
